import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useSelector } from 'react-redux';

import { createStyles, Grid, Theme, withStyles, WithStyles } from '@material-ui/core';
import classnames from 'classnames';
import { get } from 'lodash';
import { useAppointmentCancel, useAppointmentConfirm } from 'requests/resources/appointment';
import { TAppointment } from 'ui-model';

import { Setting } from '@lib/resources/types';
import { selectUserSettings } from '@lib/selectors';

import useAppointmentsActionsOptions from 'hooks/useAppointmentsActionsOptions';

import Button from './Button';
import DialogAppointmentManagement, { DialogAppointmentManagementProps } from './DialogAppointmentManagement';
import Typography from './Typography';

const messages = defineMessages({
  actionConfirm: {
    id: 'components.v2.ToolbarAppointmentActions.actionConfirm',
    defaultMessage: 'Confirm Appointment',
  },
  actionReschedule: {
    id: 'components.v2.ToolbarAppointmentActions.actionReschedule',
    defaultMessage: 'Reschedule Appointment',
  },
  actionRescheduleCompact: {
    id: 'components.v2.ToolbarAppointmentActions.actionRescheduleCompact',
    defaultMessage: 'Reschedule',
  },
  actionCancel: {
    id: 'components.v2.ToolbarAppointmentActions.actionCancel',
    defaultMessage: 'Cancel Appointment',
  },
  bookNewAppt: {
    id: 'components.v2.ToolbarAppointmentActions.bookNewAppt',
    defaultMessage: 'Book New Appointment',
  },
  or: {
    id: 'components.v2.ToolbarAppointmentActions.or',
    defaultMessage: 'or',
  },
  changedYourMind: {
    id: 'components.v2.ToolbarAppointmentActions.changedYourMind',
    defaultMessage: 'Changed your mind?',
  },
  needToMakeChanges: {
    id: 'components.v2.ToolbarAppointmentActions.needToMakeChanges',
    defaultMessage: 'Need to make changes?',
  },
});

const styles = ({ custom: { palette }, palette: { primary, secondary } }: Theme) =>
  createStyles({
    defaultButton: {
      fontWeight: 600,
      minHeight: 40,
    },
    cancel: {
      color: `${palette.colorDanger}`,
      border: `1px solid ${palette.colorDanger}`,
    },
    reschedule: {
      border: `1px solid ${primary.main}`,
    },
    orangeColor: {
      color: `${secondary.main}`,
      cursor: 'pointer',
    },
  });

export interface ToolbarAppointmentActionsProps extends WithStyles<typeof styles> {
  appointment: TAppointment;
}

const ToolbarAppointmentActions: React.FC<ToolbarAppointmentActionsProps> = ({ appointment, classes, ...rest }) => {
  const { formatMessage } = useIntl();
  const settings = useSelector(selectUserSettings);
  const [submitting, setSubmitting] = useState(false);
  const {
    includeConfirm: overrideIncludeConfirm,
    includeCancel: overrideIncludeCancel,
    // includeReschedule: overrideIncludeReschedule,
  } = useAppointmentsActionsOptions(appointment);
  const hasSettingsToOverrideFrom =
    get(settings, 'settingsToOverrideFrom.appointmentTypes', []).length > 0 ||
    get(settings, 'settingsToOverrideFrom.facilities', []).length > 0;

  const [openConfirmCancel, setOpenConfirmCancel] = useState(false);
  const [dialogType, setDialogType] = useState<DialogAppointmentManagementProps['dialogType']>('cancel');
  const appointmentConfirm = useAppointmentConfirm();
  const appointmentCancel = useAppointmentCancel();

  // Configure actions visibility
  // https://app.clickup.com/t/vhfwpe
  const settingsReminder: Setting['reminder'] = get(settings, 'reminder', {});

  // Confirm
  let includeConfirm =
    appointment.status !== 'confirmed' &&
    // reminder.replies
    (settingsReminder.replies === 'allowed' || settingsReminder.replies === 'confirmationOnly');

  // reminder.allowConfirmAfterCancel
  if (appointment.status === 'cancelled' && !settingsReminder.allowConfirmAfterCancel) {
    includeConfirm = false;
  }

  includeConfirm = hasSettingsToOverrideFrom ? overrideIncludeConfirm : includeConfirm;

  // Cancel
  let includeCancel =
    appointment.status !== 'cancelled' &&
    // reminder.replies
    settingsReminder.replies === 'allowed';

  // reminder.onConfirmation & reminder.repliesForLastReminder
  if (appointment.status === 'confirmed' && settingsReminder.onConfirmation === 'keepLastReminder') {
    includeCancel = settingsReminder.repliesForLastReminder === 'allowed';
  }

  // reminder.allowCancelAfterConfirm
  if (appointment.status === 'confirmed' && !settingsReminder.allowCancelAfterConfirm) {
    includeCancel = false;
  }

  includeCancel = hasSettingsToOverrideFrom ? overrideIncludeCancel : includeCancel;

  // Reschedule
  const includeReschedule = false;
  // get(appointment, 'status') !== 'cancelled' &&
  // get(settingsReminder, 'allowSelfSchedule', false) && // reminder.allowSelfSchedule
  // get(settingsReminder, 'allowSelfReschedule', false); // reminder.allowSelfReschedule

  const handleDialogOpening = (type: DialogAppointmentManagementProps['dialogType']) => {
    setDialogType(type);
    setOpenConfirmCancel(true);
  };

  const handleDialogSubmit = (type: string) => {
    // todo: once we supports reschedule, add its events
    if (type === 'reschedule') return null;

    setOpenConfirmCancel(false);
    setSubmitting(true);
    appointmentCancel.mutate(appointment._id, {
      onSettled: () => setSubmitting(false),
    });
  };

  if (!includeConfirm && !includeReschedule && !includeCancel) return null;

  if (get(appointment, 'status') === 'cancelled') {
    return includeConfirm ? (
      <Grid item container direction="column" spacing={8}>
        <Typography variant="supportW500F14" align="center">
          {formatMessage(messages.changedYourMind)}
        </Typography>
        <Grid item container spacing={8} {...rest}>
          <Grid item xs={12}>
            <Button
              data-lh-id="btn-ToolbarAppointmentActions-confirm"
              className={classes.defaultButton}
              variant="contained"
              color="primary"
              fullWidth
              disabled={submitting}
              onClick={() => {
                setSubmitting(true);
                appointmentConfirm.mutate(appointment._id, {
                  onSettled: () => setSubmitting(false),
                });
              }}
            >
              {formatMessage(messages.actionConfirm)}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    ) : null;
  }

  if (get(appointment, 'status') === 'confirmed') {
    return (
      <Grid item container justify="center">
        {includeReschedule && (
          <Typography
            underline
            className={classes.orangeColor}
            variant="bW600F14"
            data-lh-id="btn-ToolbarAppointmentActions-rescheduleCompact"
            onClick={() => handleDialogOpening('reschedule')}
          >
            {formatMessage(messages.actionRescheduleCompact)}
          </Typography>
        )}
        {includeReschedule && <Typography variant="bW600F14">&nbsp;{formatMessage(messages.or)}&nbsp;</Typography>}
        {includeCancel && (
          <Grid item container direction="column">
            <Typography variant="supportW500F14" align="center">
              {formatMessage(messages.needToMakeChanges)}
            </Typography>
            <Typography
              align="center"
              underline
              className={classes.orangeColor}
              variant="bW600F14"
              data-lh-id="btn-ToolbarAppointmentActions-cancelCompact"
              onClick={() => handleDialogOpening('cancel')}
            >
              {formatMessage(messages.actionCancel)}
            </Typography>
          </Grid>
        )}
        <DialogAppointmentManagement
          dialogType={dialogType}
          open={openConfirmCancel}
          onClose={() => setOpenConfirmCancel(false)}
          onSubmit={() => handleDialogSubmit(dialogType)}
        />
      </Grid>
    );
  }

  return (
    <>
      <Grid item container spacing={8} {...rest}>
        {includeConfirm && (
          <Grid item xs={12}>
            <Button
              data-lh-id="btn-ToolbarAppointmentActions-confirm"
              className={classes.defaultButton}
              variant="contained"
              color="primary"
              fullWidth
              disabled={submitting}
              onClick={() => {
                setSubmitting(true);
                appointmentConfirm.mutate(appointment._id, {
                  onSettled: () => setSubmitting(false),
                });
              }}
            >
              {formatMessage(messages.actionConfirm)}
            </Button>
          </Grid>
        )}
        <Grid container item spacing={8}>
          {includeReschedule && (
            <Grid item xs={12}>
              <Button
                data-lh-id="btn-ToolbarAppointmentActions-reschedule"
                className={classnames(classes.defaultButton, classes.reschedule)}
                variant="outlined"
                color="primary"
                fullWidth
                disabled={submitting}
                onClick={() => handleDialogOpening('reschedule')}
              >
                {formatMessage(messages.actionReschedule)}
              </Button>
            </Grid>
          )}
          {includeCancel && (
            <Grid item xs={12}>
              <Button
                data-lh-id="btn-ToolbarAppointmentActions-cancel"
                className={classnames(classes.defaultButton, classes.cancel)}
                variant="outlined"
                fullWidth
                disabled={submitting}
                onClick={() => handleDialogOpening('cancel')}
              >
                {formatMessage(messages.actionCancel)}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      <DialogAppointmentManagement
        dialogType={dialogType}
        open={openConfirmCancel}
        onClose={() => setOpenConfirmCancel(false)}
        onSubmit={() => handleDialogSubmit(dialogType)}
      />
    </>
  );
};

export default withStyles(styles)(ToolbarAppointmentActions);
